/* eslint-disable no-param-reassign */
import { useEffect, useState, useCallback, useMemo, cloneElement, MutableRefObject } from 'react';
import { Box, BoxProps, Flex } from '@chakra-ui/react';
import ReactDataGrid from '@inovua/reactdatagrid-enterprise';

import {
  TypeColumn,
  TypeComputedProps,
  TypeDataGridProps,
  TypeDataSource,
} from '@inovua/reactdatagrid-enterprise/types';
import Bar from 'components/DataTable/Bar';
import Footer from 'components/DataTable/Footer';
import 'components/DataTable/Datatable.css';
import { useLocalStorage, useVariable, useCreateCSV, useToast } from 'hooks';
import PageActions, { PageActionsProps } from 'components/DataTable/PageActions';
import { IAction } from 'components/DataTable/Actions';
import { checkIfError, getUtcOffset } from 'utils/helpers';
import { mapFilterBy } from 'utils/dataTable';
import { TableItem } from 'types';
import { ICSVCreateRequest } from '../../../server/types';

type RenderRowParams = Parameters<NonNullable<TypeDataGridProps['renderRow']>>[0];

export type RowProps = RenderRowParams & {
  onDoubleClick: (event: PointerEvent) => void;
};

type Column = Omit<TypeColumn, 'operator' | 'name'> & {
  operator?: string;
  name?: string;
};

type TableProps = Omit<Partial<TypeDataGridProps>, 'columns' | 'dataSource'> & {
  dataSource?: TypeDataSource;
  onLoadingChange?: React.Dispatch<React.SetStateAction<boolean>>;
};

type DataTableProps<T extends object> = Partial<Omit<TypeDataGridProps, 'columns'>> & {
  id: string;
  tableProps?: TableProps;
  exportParams?: ICSVCreateRequest;
  rows?: T[];
  actions?: IAction<T>[];
  menuItems?: React.ReactElement[];
  onRowDoubleClick?: (event: MouseEvent, rowProps: RowProps) => void;
  selectedItems?: Record<string, T>;
  onFilter?: (rows: T[]) => T[];
  onSelect?: (item: T) => void;
  showFooter?: boolean;
  pageActions?: PageActionsProps['pageActions'];
  total?: number;
  columns: Column[];
  tableStyles?: Record<string, string | boolean>;
  tableContainerProps?: BoxProps;
};

type BatchColumnInfo = {
  column: TypeColumn;
  width?: number;
  flex?: number;
  size?: number;
};

type ColumnVisibilityParams = {
  column: TypeColumn;
  visible: boolean;
};

type ColumnVisibility = {
  defaultVisible?: boolean;
};

type ColumnFilter = {
  name: string;
  operator: string;
  type: string;
  value: string;
};

type ColumnSize = {
  width: number;
};

type DefaultColumnSize = {
  defaultWidth?: number;
};

type ConfigChangeParams = {
  newColumnOrder?: Parameters<NonNullable<TypeDataGridProps['onColumnOrderChange']>>[0];
  newColumnVisibility?: Record<string, ColumnVisibility>;
  newColumnSizes?: Record<string, ColumnSize>;
};

type SelectParams<T extends object> = Parameters<NonNullable<TypeDataGridProps['onSelectionChange']>>[0] & {
  data?: object | T[];
};

const DataTable = <T extends object>({
  id,
  rows,
  columns,
  actions = [],
  onRowDoubleClick,
  selectedItems,
  onFilter,
  onSelect,
  tableProps,
  tableContainerProps,
  tableStyles,
  exportParams,
  loading,
  filterTypes,
  menuItems = [],
  pagination = false,
  showFooter = true,
  showColumnMenuTool = true,
  livePagination,
  pageActions,
  total,
}: DataTableProps<T>) => {
  const [selected, setSelected] = useState<Record<string, T>>({});
  const [gridRef, setGridRef] = useState<MutableRefObject<TypeComputedProps | null>>();
  const [tableRows, setTableRows] = useState<T[]>([]);
  const [filtering, setFiltering] = useState(false);
  const [currentColumns, setCurrentColumns] = useState<TypeColumn[]>([]);
  const [columnOrder, setColumnOrder] = useState<string[]>([]);
  const [columnVisibility, setColumnVisibility] = useVariable<ConfigChangeParams['newColumnVisibility']>({});
  const [columnSizes, setColumnSizes] = useVariable<Record<string, DefaultColumnSize>>({});

  const [showToast] = useToast();
  const [createCSV, { error: createCSVError, loading: createCSVLoading }] = useCreateCSV();
  const [storageValue, setStorageValue] = useLocalStorage(`variant-${process.env.MIX_APP_ENV}-grid-${id}`);

  const handleRowSelection = ({ selected: selection, data, unselected }: SelectParams<T>) => {
    if (!pagination || selection !== true || (!unselected && !data)) {
      setSelected(selection as Record<string, T>);

      return;
    }

    if (unselected && Object.keys(unselected).length > 0) {
      const unselectedKeys = Object.keys(unselected);
      setSelected((prevSelected) =>
        Object.entries(prevSelected).reduce((acc, prevSelection: any) => {
          if (unselectedKeys.includes(prevSelection)) return acc;

          return { ...acc, prevSelection };
        }, {})
      );
    }

    const tableData = data ?? [];

    setSelected(
      (tableData as T[]).reduce(
        (acc: Record<string, T>, selectedItem: T) => ({
          ...acc,
          [(selectedItem as Record<string, string>)[tableProps?.idProperty ?? '_id']]: selectedItem,
        }),
        {}
      )
    );
  };

  const handleSelectionChange = useCallback(
    (params: SelectParams<T>) => {
      handleRowSelection(params);

      if (onSelect) onSelect(params.data as T);
    },
    [onSelect]
  );

  const handleGetFilter = () => {
    if (livePagination) return mapFilterBy(gridRef?.current?.computedFilterValue);

    const filterKey = tableProps?.idProperty ?? '_id';

    const values = gridRef?.current?.data ? gridRef?.current?.data.map((item) => item[filterKey]) : null;

    return (values ?? []).length < (rows ?? []).length
      ? {
          [filterKey]: {
            $in: values,
          },
        }
      : {};
  };

  const handleCreateCSV = async () => {
    if (!exportParams) return;

    const utcOffset = getUtcOffset();

    const filter = handleGetFilter();

    const csvResult = await createCSV({
      ...exportParams,
      utcOffset,
      filter,
    });

    if (checkIfError(csvResult)) return;

    (window as any).open(csvResult, '_self').opener = null;
  };

  useEffect(() => {
    setSelected(selectedItems ?? {});
  }, [selectedItems]);

  useEffect(() => {
    if (!rows) return;

    setTableRows(onFilter ? onFilter(rows) : rows);
  }, [rows]);

  useEffect(() => {
    if (createCSVError) {
      showToast({ status: 'error', message: createCSVError });
    }
  }, [createCSVError]);

  useEffect(() => {
    const savedGrid = storageValue ? JSON.parse(storageValue) : null;

    if (savedGrid && id) {
      const newColumns = columns
        .filter((col) => col.name || col.id)
        .map((col) => {
          const identifier = col.name ?? col.id;

          const { minWidth, defaultFlex, ...colParams } = col;

          const widthParams =
            identifier && savedGrid.columnSizes[identifier]?.width
              ? {
                  defaultWidth: savedGrid.columnSizes[identifier]?.width,
                }
              : {
                  minWidth,
                  defaultFlex,
                };

          const savedVisibility = identifier ? savedGrid.columnVisibility[identifier]?.defaultVisible : undefined;

          return {
            ...colParams,
            ...widthParams,
            defaultVisible: savedVisibility ?? col.defaultVisible ?? true,
          };
        });

      setCurrentColumns(newColumns);

      // Support new columns added after someone's view has been saved if id hasn't been changed
      setColumnOrder([
        ...savedGrid.columnOrder,
        ...newColumns
          .filter(
            (newColumn) =>
              !savedGrid.columnOrder.includes(newColumn.name) && !savedGrid.columnOrder.includes(newColumn.id)
          )
          .map((newColumn) => newColumn.name ?? newColumn.id),
      ]);
      setColumnVisibility(savedGrid.columnVisibility);
      setColumnSizes(savedGrid.columnSizes);
    } else {
      setCurrentColumns(columns);
      setColumnOrder(
        columns.reduce((acc: string[], column) => {
          const identifier = column.name ?? column.id;

          return !identifier ? acc : [...acc, identifier];
        }, [])
      );
      setColumnVisibility(
        columns.reduce((acc: ConfigChangeParams['newColumnVisibility'], { defaultVisible, ...column }) => {
          const identifier = column.name ?? column.id;

          return !identifier
            ? acc
            : {
                ...acc,
                [identifier]: { defaultVisible },
              };
        }, {})
      );
      setColumnSizes(
        columns.reduce((acc: Record<string, DefaultColumnSize>, { defaultWidth, ...column }) => {
          const identifier = column.name ?? column.id;

          return !identifier
            ? acc
            : {
                ...acc,
                [identifier]: { defaultWidth },
              };
        }, {})
      );
    }
  }, [columns]);

  useEffect(() => {
    if (filtering || !gridRef?.current?.clearAllFilters) return;
    gridRef.current.clearAllFilters();
  }, [filtering]);

  const handleConfigChange = ({ newColumnOrder, newColumnVisibility, newColumnSizes }: ConfigChangeParams) => {
    setStorageValue(
      JSON.stringify({
        columnOrder: newColumnOrder ?? columnOrder,
        columnVisibility: newColumnVisibility ?? columnVisibility,
        columnSizes: newColumnSizes ?? columnSizes,
      })
    );
  };

  const handleCustomSelect = (callback: IAction<T>['callback']) => {
    const ids = Object.values(selected as Record<string, TableItem>).map(({ _id, id: itemId }) => itemId ?? _id);

    if (!callback) return;
    callback({ selected, ids } as T, () => {
      setSelected({});
    });
  };

  const handleColumnOrderChange = useCallback(
    (newColumnOrder: Parameters<NonNullable<TypeDataGridProps['onColumnOrderChange']>>[0]) => {
      setColumnOrder(newColumnOrder);
      handleConfigChange({ newColumnOrder });
    },
    [handleConfigChange]
  );

  const handleBatchColumnResize = (batchColumnInfo: BatchColumnInfo[]) => {
    const colWidths = batchColumnInfo.reduce((acc, { column, size, width }) => {
      const identifier = column.name ?? column.id;

      return !identifier
        ? acc
        : {
            ...acc,
            [identifier]: { width: width ?? size },
          };
    }, {});

    const newColumnSizes = { ...columnSizes, ...colWidths };

    setColumnSizes(newColumnSizes);

    handleConfigChange({ newColumnSizes });
  };

  const handleColumnVisibilityChange = ({ column, visible }: ColumnVisibilityParams) => {
    if (!column.name) return;

    const newColumnVisibility = {
      ...columnVisibility,
      [column.name]: { defaultVisible: visible },
    };

    setColumnVisibility(newColumnVisibility);

    handleConfigChange({ newColumnVisibility });
  };

  const handleRenderRow = useCallback(
    (rowProps: Parameters<NonNullable<TypeDataGridProps['renderRow']>>[0]) => {
      const { onDoubleClick } = rowProps as RowProps;

      (rowProps as RowProps).onDoubleClick = (event: PointerEvent) => {
        if (onRowDoubleClick) {
          onRowDoubleClick(event, rowProps as RowProps);
        }

        if (onDoubleClick) {
          onDoubleClick(event);
        }
      };

      return undefined;
    },
    [onRowDoubleClick]
  );

  const filters = useMemo(
    () =>
      columns
        .filter(({ name }) => name)
        .map(({ name, type, operator }) => ({
          name,
          operator: operator ?? 'contains',
          type: type ?? 'string',
          value: '',
        })),
    [columns]
  );

  const renderRowContextMenu = useCallback(
    (
      menuProps: Parameters<NonNullable<TypeDataGridProps['renderRowContextMenu']>>[0],
      { rowProps }: Parameters<NonNullable<TypeDataGridProps['renderRowContextMenu']>>[1]
    ) => {
      menuProps.autoDismiss = true;
      menuProps.className = `mv-context-menu ${menuItems && menuItems.length > 0 ? 'mv-context-menu--enabled' : ''}`;
      if (!menuProps.items || menuProps.items.length === 0) {
        menuProps.items = menuItems.map((MenuItem) => ({
          label: cloneElement(MenuItem, { rowProps }),
        }));
      }
    },
    [menuItems]
  );

  const dataTableLoading = loading || currentColumns.length === 0 || !currentColumns;

  const exportActions = exportParams
    ? [
        {
          type: 'download',
          callback: handleCreateCSV,
          loading: createCSVLoading,
          title: 'Export to CSV',
        },
      ]
    : [];

  return (
    <Flex justifyContent="center" h="full" data-testid="data-table">
      <Box minW="90%" maxW={800} {...tableContainerProps}>
        <PageActions pageActions={pageActions} />
        <Bar<T>
          selected={selected}
          onCustomSelect={handleCustomSelect}
          onFilter={() => {
            setFiltering((prevFiltering) => !prevFiltering);
          }}
          showViewInfo={!!onRowDoubleClick}
          filtering={filtering}
          actions={[...exportActions, ...actions]}
          loading={loading}
        />
        <ReactDataGrid
          licenseKey={process.env.REACT_APP_DATA_GRID_LICENSE_KEY}
          idProperty="_id"
          selected={selected}
          checkboxColumn
          enableSelection
          showCellBorders="horizontal"
          onSelectionChange={handleSelectionChange}
          style={{ minHeight: 400, ...tableStyles }}
          columns={currentColumns}
          columnOrder={columnOrder}
          showZebraRows={false}
          loading={dataTableLoading}
          defaultFilterValue={filters as ColumnFilter[]}
          renderRowContextMenu={renderRowContextMenu}
          dataSource={tableRows}
          onReady={setGridRef}
          enableFiltering={filtering}
          virtualized={process.env.NODE_ENV !== 'test'}
          pagination={pagination}
          onColumnOrderChange={handleColumnOrderChange}
          onBatchColumnResize={handleBatchColumnResize}
          onColumnVisibleChange={handleColumnVisibilityChange}
          renderRow={handleRenderRow}
          showColumnMenuTool={showColumnMenuTool}
          filterTypes={{
            // Workaround for ReactDataGrid not matching it's own types
            ...(ReactDataGrid.defaultProps.filterTypes as any),
            ...filterTypes,
          }}
          scrollProps={{
            autoHide: false,
            alwaysShowTrack: true,
            scrollThumbWidth: 6,
            scrollThumbOverWidth: 6,
          }}
          {...(tableProps ?? {})}
        />

        <Footer rows={rows} total={total} show={showFooter} selectedItems={selected} loading={dataTableLoading} />
      </Box>
    </Flex>
  );
};

export * from './dataRenderers';
export * from './dataSorting';

export default DataTable;
